import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { IClinicApiRow, IClinicOption, IClinicTagConfigUpdateBody } from "@/api_framework/api_modals/ClinicTagConfig";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useCouponUserPerSonaListAPI } from "@/app/hooks/api_hooks/coupon/useCouponUserPerSonaListAPI";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { useClinicTagConfigAPI } from "@/app/hooks/api_hooks/quick_commerce/useClinicTagConfigAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { BackLink, RibbonPreview, TileAvatar } from "../../HomeTileConfig/components";
import { cardClassName } from "../../HomeTileConfig/interface";
import { validatePersonaCombination } from "../../HomeTileConfig/personaExpression";
import {
  DEFAULT_VALUES,
  IClinicTagFormFields,
  buildChanges,
  buildSaveBody,
  toClinicOptions,
  toFormValues,
  toRuleKey,
} from "../interface";
import { TagRuleCard } from "./TagRuleCard";

const ClinicHero = ({ clinic, isEditMode }: { clinic?: IClinicOption; isEditMode: boolean }) => (
  <div className="relative overflow-hidden rounded-xl border border-[#193A6A]/15 bg-gradient-to-r from-[#EEF5FF] via-white to-white shadow-sm">
    <span className="absolute inset-y-0 left-0 w-1 bg-[#193A6A]" aria-hidden="true" />
    <div className="flex flex-wrap items-center gap-4 py-4 pl-6 pr-5">
      <TileAvatar
        name={clinic?.clinic_name ?? "?"}
        className="h-16 w-16 rounded-xl bg-white p-2.5 text-lg shadow-sm ring-1 ring-[#193A6A]/15"
      />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#2B5DA8]">
          {isEditMode ? "Edit Rule" : "Add Rule"}
        </div>
        <h1 className="truncate text-lg font-semibold leading-7 text-gray-900">{clinic?.clinic_name ?? "Clinic"}</h1>
        <p className="text-xs text-gray-500">Controls the tag shown on this clinic.</p>
      </div>
    </div>
  </div>
);

const TagPreviewCard = ({ clinic }: { clinic?: IClinicOption }) => {
  const [chipText = "", ribbon, status] = useWatch<IClinicTagFormFields, ["chip_txt", "ribbon", "status"]>({
    name: ["chip_txt", "ribbon", "status"],
  });
  const isActive = status === "ACTIVE";

  return (
    <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:w-[380px]">
      <div className={cardClassName}>
        <div className="border-b border-slate-100 px-5 py-3">
          <div className="text-sm font-semibold text-gray-900">Preview</div>
          <p className="mt-0.5 text-[11px] text-gray-500">How the tag reads on the clinic.</p>
        </div>
        <div className="bg-slate-50 px-6 py-10">
          <div className="relative rounded-3xl border border-slate-200 bg-white px-5 pb-5 pt-7 shadow-md">
            {chipText.trim() && (
              <div className="absolute -top-3 left-4 z-10 max-w-[calc(100%-2rem)]">
                <RibbonPreview text={chipText} style={ribbon} isActive={isActive} size="lg" />
              </div>
            )}
            <div className="flex items-center gap-4">
              <TileAvatar
                name={clinic?.clinic_name ?? "?"}
                className="h-16 w-16 rounded-2xl bg-[#EEF5FF] p-2 text-lg"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-lg font-semibold text-slate-800">{clinic?.clinic_name ?? "Clinic"}</div>
                {!chipText.trim() && <p className="mt-1 text-xs text-slate-400">Enter tag text to preview.</p>}
              </div>
            </div>
          </div>
        </div>
        <div className="divide-y divide-slate-100 border-t border-slate-100 px-5 py-2">
          <div className="flex items-center justify-between gap-3 py-2">
            <span className="text-[11px] text-slate-500">Clinic</span>
            <span className="truncate text-[11px] font-semibold text-slate-700">{clinic?.clinic_name ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between gap-3 py-2">
            <span className="text-[11px] text-slate-500">Status</span>
            <span className="truncate text-[11px] font-semibold text-slate-700">
              {isActive ? "Active" : "InActive"}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
};

const FormActions = ({ isSaving, onCancel }: { isSaving: boolean; onCancel: () => void }) => (
  <div className="sticky bottom-0 z-10 mt-4 flex items-center justify-end gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
    <button
      type="button"
      className="rounded-lg border border-slate-200 bg-white px-8 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
      onClick={onCancel}
      disabled={isSaving}
    >
      Back
    </button>
    <button
      type="submit"
      className="rounded-lg bg-[#193A6A] px-8 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#132c52] disabled:cursor-not-allowed disabled:opacity-60"
      disabled={isSaving}
    >
      {isSaving ? "Saving…" : "Save Configuration"}
    </button>
  </div>
);

const ClinicTagOnboard = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const configId = params.get("config_id");
  const clinicId = params.get("clinic_id") ?? "";
  const isEditMode = Boolean(configId);

  const { execute: fetchUserPersonas, userpersona, isLoading: isPersonaListLoading } = useCouponUserPerSonaListAPI();
  const { execute: fetchClinics, clinicList } = useClinicListAPI();
  const { configList, isListLoading, isSaving, fetchClinicConfigs, saveConfig } = useClinicTagConfigAPI();

  const [isMissingConfig, setIsMissingConfig] = useState<boolean>(false);

  const formHook = useForm<IClinicTagFormFields>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: DEFAULT_VALUES,
  });
  const { getValues, handleSubmit, reset, trigger } = formHook;

  const clinic = useMemo(
    () => toClinicOptions(clinicList as IClinicApiRow[]).find((option) => option.clinic_id === clinicId),
    [clinicList, clinicId],
  );

  useEffect(() => {
    fetchUserPersonas();
  }, [fetchUserPersonas]);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  useEffect(() => {
    if (!clinicId) return;
    let isCurrent = true;
    fetchClinicConfigs(clinicId).then((configs) => {
      if (!isCurrent || !configs || !configId) return;
      const config = configs.find((item) => item.id === configId);
      setIsMissingConfig(!config);
      if (!config) return;
      reset(toFormValues(config));
    });
    return () => {
      isCurrent = false;
    };
  }, [clinicId, configId, fetchClinicConfigs, reset]);

  const activeRuleKeys = useMemo(
    () =>
      new Set(
        configList
          .filter((config) => config.id !== configId && config.status === "ACTIVE" && config.persona_rule)
          .map((config) => toRuleKey(config.persona_rule)),
      ),
    [configList, configId],
  );

  const validatePersonaField = useCallback(
    (value: string): true | string => {
      if (isPersonaListLoading) return "Personas are still loading.";
      if (!userpersona.length) return "Couldn't load the persona list, so the combination can't be checked.";
      if (!value.trim() && getValues("persona_logic_mode") === "BASIC") {
        return "Select at least one persona to include or exclude.";
      }

      const parsed = validatePersonaCombination(value, userpersona);
      if (!parsed.isValid) return parsed.error as string;
      if (getValues("status") !== "ACTIVE") return true;

      return parsed.canonical && activeRuleKeys.has(parsed.canonical)
        ? "An active tag already exists for this clinic and persona combination."
        : true;
    },
    [activeRuleKeys, getValues, isPersonaListLoading, userpersona],
  );

  useEffect(() => {
    if (isPersonaListLoading || !getValues("persona_combination")) return;
    void trigger("persona_combination");
  }, [isPersonaListLoading, userpersona, trigger, getValues]);

  const clinicUrl = `${getRouteKey("CLINIC_TAG_SETUP", "url")}${
    clinicId ? `?clinic_id=${encodeURIComponent(clinicId)}` : ""
  }`;

  const handleCancel = () => {
    reset(DEFAULT_VALUES);
    navigate(clinicUrl);
  };

  const onSubmit = async (data: IClinicTagFormFields) => {
    if (!clinicId) {
      toast.error("This configuration has no clinic attached. Start again from the clinic list.");
      return;
    }

    const parsedPersona = validatePersonaCombination(data.persona_combination, userpersona);
    if (!parsedPersona.rule) {
      toast.error(parsedPersona.error || "Enter a valid persona combination.");
      return;
    }

    const body = buildSaveBody(clinicId, data, parsedPersona.rule);

    if (!configId) {
      if (await saveConfig(body)) navigate(clinicUrl);
      return;
    }

    const savedConfig = configList.find((config) => config.id === configId);
    const changes: IClinicTagConfigUpdateBody = savedConfig
      ? buildChanges(body, savedConfig)
      : { configuration: body.configuration, chip_txt: body.chip_txt, ribbon: body.ribbon, status: body.status };

    if (!Object.keys(changes).length) {
      toast.success("No changes to save.");
      navigate(clinicUrl);
      return;
    }

    if (await saveConfig(changes, configId)) navigate(clinicUrl);
  };

  const onInvalid = () => toast.error("Fix the highlighted fields before saving.");

  if (!clinicId || (isEditMode && isMissingConfig)) {
    return (
      <div className="px-5 py-4">
        <BackLink label="Back to configurations" onClick={handleCancel} />
        <div className={`${cardClassName} px-5 py-10 text-center`}>
          <p className="text-sm font-semibold text-slate-700">This configuration could not be opened.</p>
          <p className="mt-1 text-xs text-slate-500">
            Open it from its clinic on the configuration list so the clinic is carried along.
          </p>
        </div>
      </div>
    );
  }

  if (isListLoading) {
    return (
      <div className="bg-white px-5 py-4">
        <SkeletonType1 />
        <SkeletonType1 className="mt-8" />
      </div>
    );
  }

  return (
    <div className="px-5 py-4">
      <FormProvider {...formHook}>
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <BackLink label="Back to configurations" onClick={handleCancel} />

          <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
            <div className="min-w-0 flex-1 space-y-4">
              <ClinicHero clinic={clinic} isEditMode={isEditMode} />
              <TagRuleCard
                personas={userpersona}
                isPersonaListLoading={isPersonaListLoading}
                validatePersona={validatePersonaField}
              />
            </div>
            <TagPreviewCard clinic={clinic} />
          </div>

          <FormActions isSaving={isSaving} onCancel={handleCancel} />
        </form>
      </FormProvider>
    </div>
  );
};

export default ClinicTagOnboard;
